import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    Chip,
    Divider,
    IconButton,
    Tooltip,
    Alert,
    Grid,
    TextField
} from "@mui/material";
import {
    Edit,
    Save,
    Cancel,
    ArrowBack,
    Assignment
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import InvestmentForm from "./InvestmentForm";
import ImageGallery from "./ImageGallery";
import { formatCurrency } from "../capital/capitalUtils";
import { useRole } from "../domain/roles/role.middleware";
import { PermissionGuard } from "../domain/roles/role.guard";
import { PERMISSIONS } from "../domain/roles/role.constants";

const estadosFeedbackAporte = ["Invertida", "Cerrada exitosa", "Cerrada fallida"];
const estadosFeedbackCierre = ["Cerrada exitosa", "Cerrada fallida"];

const OpportunityDetail = ({ opportunity, _onClose, _onInvestmentComplete }) => {
    const { i18n } = useTranslation();
    const { roleService } = useRole();
    const navigate = useNavigate();
    const [investmentFormOpen, setInvestmentFormOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState({});
    const [loading] = useState(false);
    const [error] = useState("");

    useEffect(() => {
        if (opportunity) {
            setEditData({
                title: opportunity.title || "",
                description: opportunity.description || "",
                amount: opportunity.amount || 0,
                minimumInvestment: opportunity.minimumInvestment || 0,
                expectedReturn: opportunity.expectedReturn || 0,
                duration: opportunity.duration || 0,
                riskLevel: opportunity.riskLevel || "Medium",
                status: opportunity.status || "active"
            });
        }
    }, [opportunity]);

    if (loading) {
        return (
            <Box sx={{ py: 4 }}>
                <Typography>Cargando oportunidad...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ py: 4 }}>
                <Alert severity="error">
                    <Typography variant="h6">Error</Typography>
                    <Typography>{error}</Typography>
                </Alert>
            </Box>
        );
    }

    if (!opportunity) {
        return (
            <Box sx={{ py: 4 }}>
                <Alert severity="warning">
                    <Typography>Oportunidad no encontrada</Typography>
                </Alert>
            </Box>
        );
    }

    const handleInvestNow = () => {
        setInvestmentFormOpen(true);
    };

    const handleInvestmentComplete = () => {
        setInvestmentFormOpen(false);
    
        window.dispatchEvent(new CustomEvent("dashboard-refresh"));
    
        setTimeout(() => {
            // navigate("/dashboard"); // This line was removed as per the new_code
        }, 1000);
    };

    const handleEdit = () => {
        setIsEditing(true);
    };

    const handleSave = async () => {
        try {
            // Here you would save the changes to the database
            setIsEditing(false);
            // Trigger refresh of opportunity data
            window.dispatchEvent(new CustomEvent("opportunity-updated"));
        } catch (error) {
            // Error saving opportunity
        }
    };

    const handleCancel = () => {
        setIsEditing(false);
        // Reset edit data to original values
        setEditData({
            title: opportunity.title || "",
            description: opportunity.description || "",
            amount: opportunity.amount || 0,
            minimumInvestment: opportunity.minimumInvestment || 0,
            expectedReturn: opportunity.expectedReturn || 0,
            duration: opportunity.duration || 0,
            riskLevel: opportunity.riskLevel || "Medium",
            status: opportunity.status || "active"
        });
    };

    const handleInputChange = (field, value) => {
        setEditData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const getRiskColor = (riskLevel) => {
        switch (riskLevel?.toLowerCase()) {
        case "low": return "success";
        case "medium": return "warning";
        case "high": return "error";
        default: return "default";
        }
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
        case "active": return "success";
        case "pending": return "warning";
        case "closed": return "error";
        case "draft": return "default";
        default: return "default";
        }
    };

    const isAgentAssigned = () => {
        if (!opportunity || !roleService.currentUser) return false;
        return opportunity.agentId === roleService.currentUser.uid;
    };

    const currentRole = roleService.getCurrentUserRole();
    const canInvest = currentRole === "INVESTOR" && opportunity.status === "active";
    const canEdit = currentRole === "AGENT" && isAgentAssigned();

    return (
        <Box sx={{ py: 4 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box sx={{ display: "flex", gap: 2 }}>
                    <Button 
                        startIcon={<ArrowBack />}
                        variant="text" 
                        onClick={() => navigate("/opportunities")}
                    >
              Volver a Oportunidades
                    </Button>
                    <Button 
                        startIcon={<ArrowBack />}
                        variant="text" 
                        onClick={() => navigate("/dashboard")}
                    >
              Volver al Dashboard
                    </Button>
                </Box>

                {/* Edit controls for authorized users */}
                <PermissionGuard permission={PERMISSIONS.EDIT_OPPORTUNITY}>
                    {canEdit && (
                        <Box sx={{ display: "flex", gap: 1 }}>
                            {!isEditing ? (
                                <Tooltip title="Editar oportunidad">
                                    <IconButton onClick={handleEdit} color="primary">
                                        <Edit />
                                    </IconButton>
                                </Tooltip>
                            ) : (
                                <>
                                    <Tooltip title="Guardar cambios">
                                        <IconButton onClick={handleSave} color="success">
                                            <Save />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Cancelar edición">
                                        <IconButton onClick={handleCancel} color="error">
                                            <Cancel />
                                        </IconButton>
                                    </Tooltip>
                                </>
                            )}
                        </Box>
                    )}
                </PermissionGuard>
            </Box>

            {/* Status indicators */}
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Chip 
                    label={`${opportunity.riskLevel} Risk`}
                    color={getRiskColor(opportunity.riskLevel)}
                    size="small"
                />
                <Chip 
                    label={opportunity.propertyType}
                    color="primary"
                    size="small"
                />
                <Chip 
                    label={opportunity.status}
                    color={getStatusColor(opportunity.status)}
                    size="small"
                />
                {isAgentAssigned() && (
                    <Chip 
                        label="Asignado a ti"
                        color="info"
                        size="small"
                        icon={<Assignment />}
                    />
                )}
            </Box>

            <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                    <ImageGallery 
                        media={opportunity.media || []}
                        images={opportunity.images || []}
                        title={opportunity.title}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                        {/* Title and Description */}
                        <Box>
                            {isEditing ? (
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                    <TextField
                                        type="text"
                                        value={editData.title}
                                        onChange={(e) => handleInputChange("title", e.target.value)}
                                        style={{
                                            fontSize: "2rem",
                                            fontWeight: "bold",
                                            color: "#1976d2",
                                            border: "none",
                                            borderBottom: "2px solid #1976d2",
                                            padding: "8px 0",
                                            width: "100%"
                                        }}
                                    />
                                    <TextField
                                        type="text"
                                        value={editData.description}
                                        onChange={(e) => handleInputChange("description", e.target.value)}
                                        style={{
                                            fontSize: "1rem",
                                            color: "text.secondary",
                                            border: "none",
                                            borderBottom: "1px solid #ccc",
                                            padding: "8px 0",
                                            width: "100%",
                                            minHeight: "80px",
                                            resize: "vertical"
                                        }}
                                    />
                                </Box>
                            ) : (
                                <>
                                    <Typography variant="h3" color="primary" sx={{ fontWeight: "bold", mb: 1 }}>
                                        {opportunity.title}
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary">
                                        {opportunity.description}
                                    </Typography>
                                </>
                            )}
                        </Box>

                        <Divider />

                        {/* Investment Details */}
                        <Box>
                            <Typography variant="h5" sx={{ mb: 2 }}>Detalles de Inversión</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="caption" color="text.secondary">Monto Total</Typography>
                                    <Typography variant="h6" color="primary" sx={{ fontWeight: "bold" }}>
                                        {formatCurrency(opportunity.amount ?? 0, "EUR", i18n.language)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="caption" color="text.secondary">Inversión Mínima</Typography>
                                    <Typography variant="h6" color="success.main" sx={{ fontWeight: "bold" }}>
                                        {formatCurrency(opportunity.minimumInvestment ?? 0, "EUR", i18n.language)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="caption" color="text.secondary">Retorno Esperado</Typography>
                                    <Typography variant="h6" color="secondary" sx={{ fontWeight: "bold" }}>
                                        {opportunity.expectedReturn}%
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="caption" color="text.secondary">Duración</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                                        {opportunity.duration} meses
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>

                        <Divider />

                        {/* Location */}
                        <Box>
                            <Typography variant="caption" color="text.secondary">Ubicación</Typography>
                            <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                                {opportunity.location}
                            </Typography>
                        </Box>

                        {/* Action buttons */}
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            {canInvest ? (
                                <Button 
                                    variant="contained" 
                                    size="large" 
                                    onClick={handleInvestNow}
                                    sx={{ 
                                        height: 60,
                                        fontSize: "1.1rem"
                                    }}
                                >
                    Invertir Ahora
                                </Button>
                            ) : (
                                <Alert severity="info">
                                    <Typography variant="body2">
                                        {currentRole === "INVESTOR" 
                                            ? "Esta oportunidad no está disponible para inversión en este momento."
                                            : "Solo los inversores pueden realizar inversiones."
                                        }
                                    </Typography>
                                </Alert>
                            )}

                            {/* Additional actions for agents and admins */}
                            <PermissionGuard permission={PERMISSIONS.MANAGE_TASKS}>
                                {isAgentAssigned() && (
                                    <Button 
                                        variant="outlined" 
                                        startIcon={<Assignment />}
                                        onClick={() => navigate(`/agent/tasks/${opportunity.id}`)}
                                    >
                      Gestionar Tareas del Proyecto
                                    </Button>
                                )}
                            </PermissionGuard>
                        </Box>
                    </Box>
                </Grid>
            </Grid>

            <InvestmentForm
                open={investmentFormOpen}
                onClose={() => setInvestmentFormOpen(false)}
                opportunity={opportunity}
                onInvestmentComplete={handleInvestmentComplete}
            />
        </Box>
    );
};

export function InvestorFeedback({ operationId, userId, estadoOportunidad, tipo }) {
    // tipo: 'aporte' o 'cierre'
    const [score, setScore] = useState("");
    const [comment, setComment] = useState("");
    const [saved, setSaved] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
    // Cargar feedback previo si existe
        async function fetchFeedback() {
            setLoading(true);
            const op = {}; // TODO(api): Replace operationService usage with API call
            // const op = await operationService.getOperationById(operationId);
            const feedbacks = op.feedbacks || [];
            const fb = feedbacks.find(f => f.userId === userId && f.tipo === tipo);
            if (fb) {
                setScore(fb.score);
                setComment(fb.comment);
            }
            setLoading(false);
        }
        fetchFeedback();
    }, [operationId, userId, tipo]);

    const canFeedback =
    (tipo === "aporte" && estadosFeedbackAporte.includes(estadoOportunidad)) ||
    (tipo === "cierre" && estadosFeedbackCierre.includes(estadoOportunidad));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        // TODO(api): Replace operationService usage with API call
        // await operationService.addFeedbackToOperation(operationId, userId, score, comment, tipo);
        setSaved(true);
        setLoading(false);
    };

    if (!canFeedback) return null;

    return (
        <form onSubmit={handleSubmit} style={{ margin: "16px 0", background: "#f9f9f9", padding: 12, borderRadius: 8 }}>
            <h4>Tu feedback ({tipo === "aporte" ? "tras invertir" : "al cierre"})</h4>
            <label>
        Puntuación:
                <select value={score} onChange={e => setScore(e.target.value)} required disabled={loading}>
                    <option value="">-</option>
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                    <option value="5">5</option>
                </select>
            </label>
            <br />
            <label>
        Comentario:
                <input
                    type="text"
                    value={comment}
                    onChange={e => setComment(e.target.value)}
                    maxLength={200}
                    disabled={loading}
                    style={{ width: "60%" }}
                />
            </label>
            <br />
            <button type="submit" disabled={loading || saved} style={{ marginTop: 8 }}>
                {saved ? "¡Guardado!" : "Enviar feedback"}
            </button>
        </form>
    );
}

export default OpportunityDetail; 